// User Migration Service for Signup and Login Events
import { fetchUserAttributes } from '@aws-amplify/auth';
import { databaseMigrationAdapter } from './DatabaseMigrationAdapter';

interface UserData {
  id: string;
  email: string;
  name?: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  [key: string]: unknown;
}

class UserMigrationService {
  private static instance: UserMigrationService;

  static getInstance(): UserMigrationService {
    if (!UserMigrationService.instance) {
      UserMigrationService.instance = new UserMigrationService();
    }
    return UserMigrationService.instance;
  }

  private constructor() {}

  /**
   * Migrate user during signup process
   * This should be called after successful signup
   */
  async migrateUserOnSignup(userData: UserData): Promise<boolean> {
    try {
      // Prepare user data for migration
      const migrationData = this.prepareUserData(userData);
      
      const result = await databaseMigrationAdapter.handleUserOperation('CREATE', migrationData);
      
      if (result.success) {
        return true;
      } else if (result.skipped) {
        return true;
      } else {
        console.error('[USER-MIGRATION] Failed to migrate user on signup:', result);
        
        // Check for specific foreign key errors
        if (result.error?.includes('profiles_id_fkey') || result.error?.includes('null value in column "id"')) {
          console.error('[USER-MIGRATION] Database constraint error during signup - user auth setup failed');
        }
        
        return false;
      }
      
    } catch (error) {
      console.error('[USER-MIGRATION] Error during signup migration:', error);
      return false;
    }
  }

  /**
   * Check and migrate user during login process
   * This should be called after successful login
   */
  async checkAndMigrateUserOnLogin(): Promise<boolean> {
    console.log('[USER-MIGRATION] Checking user migration status on login');
    
    try {
      // Get current user attributes from Amplify
      const userAttributes = await fetchUserAttributes();
      
      if (!userAttributes.sub || !userAttributes.email) {
        console.error('[USER-MIGRATION] Missing required user attributes');
        return false;
      }

      // Prepare user data
      const userData: UserData = {
        id: userAttributes.sub,
        email: userAttributes.email,
        name: userAttributes.name,
        first_name: userAttributes.given_name,
        last_name: userAttributes.family_name,
        phone: userAttributes.phone_number,
        ...userAttributes
      };

      const migrationData = this.prepareUserData(userData);
      
      const result = await databaseMigrationAdapter.handleUserOperation('CREATE', migrationData);
      
      if (result.success) {
        return true;
      } else if (result.skipped) {
        return true;
      } else {
        console.error('[USER-MIGRATION] Failed to migrate user on login:', result);
        
        // Check for specific foreign key errors
        if (result.error?.includes('profiles_id_fkey') || result.error?.includes('null value in column "id"')) {
          console.error('[USER-MIGRATION] Database constraint error detected - user auth setup failed');
        }
        
        return false;
      }
      
    } catch (error) {
      console.error('[USER-MIGRATION] Error during login migration check:', error);
      return false;
    }
  }

  /**
   * Prepare user data for migration with complete user_usage and user_preferences
   */
  private prepareUserData(userData: UserData): Record<string, unknown> {
    const fullName = userData.name || 
      (userData.first_name && userData.last_name 
        ? `${userData.first_name} ${userData.last_name}` 
        : userData.first_name || userData.last_name || 'Unknown User');

    // Extract usage statistics from Amplify user attributes
    const transcriptionTime = parseFloat(String(userData.transcriptionTime)) || 0;
    const totalRecordings = parseInt(String(userData.transcriptionCreated)) || 0;
    const currentRecordings = parseInt(String(userData.currentTranscriptionsCreated)) || 0;
    const audioRetentionPeriod = parseInt(String(userData.audioRetentionPeriod)) || 30;

    const migrationData: Record<string, unknown> = {

      email: userData.email,
      full_name: fullName,
      audioRetentionPeriod: audioRetentionPeriod,
      transcriptionTime: transcriptionTime,
      transcriptionCreated: totalRecordings,
      currentTranscriptionsCreated: currentRecordings,

      original_amplify_id: userData.id,
      createdAt: new Date().toISOString(),
      _user_email: userData.email
    };


    migrationData.user_usage_data = {
      total_recordings_created: totalRecordings,
      total_transcription_seconds: Math.round(transcriptionTime),
      recordings_created: currentRecordings
    };



    migrationData.user_preferences_data = {
      default_view: 'cases',
      preferred_language: 'en',
      audio_file_retention_days: audioRetentionPeriod
    };



    return migrationData;
  }

  /**
   * Check if user exists in Supabase (for debugging purposes)
   */
  async checkUserExists(userId: string): Promise<boolean> {
    try {
      return true; // We use upsert, so this always succeeds
    } catch (error) {
      console.error('[USER-MIGRATION] Error checking user existence:', error);
      return false;
    }
  }
}

export const userMigrationService = UserMigrationService.getInstance(); 